// The counter's pass shelf and the words a pass is summed up in — shared by the Passes tab (spec/13 §18) and
// the Sell screen's Passes category (spec/13 §4, F-POS-01).
//
// The shelf is a convenience for the till: what it lists is what the server would sell at the counter now —
// active, sold on the `counter` channel, inside its sale window, in scope for this venue, and not free. The
// server re-checks every one of those when the sale is placed (and the purchase limits, which need the
// player's history), so nothing here is trusted. A members-only pass is listed but not sellable: memberships
// have no service yet, so the counter cannot check one (the server refuses it too).

import type { PassProduct, PassValidity } from '@clavian/api-client';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "30 days from purchase", "60 days from first use", or "Until a fixed date". */
export function validitySummary(t: Translate, validity: PassValidity): string {
  if (validity.type === 'fixed_end') return t('passes.validity.fixed');
  if (validity.type === 'days_after_first_use') return t('passes.validity.afterFirstUse', { count: validity.days ?? 0 });
  return t('passes.validity.afterPurchase', { count: validity.days ?? 0 });
}

/** The play time a pass holds: whole hours as hours ("3 h"), anything else in minutes ("90 min"). */
export function passDuration(t: Translate, durationSeconds: number): string {
  if (durationSeconds > 0 && durationSeconds % 3600 === 0) return t('sell.passes.hours', { count: durationSeconds / 3600 });
  return t('passes.minutes', { count: Math.round(durationSeconds / 60) });
}

/** Whether a pass is kept for members — shown on the counter, not sold there (see above). */
export function isMembersOnly(product: PassProduct): boolean {
  return (product.version.membersOnly ?? []).length > 0;
}

/** Whether the counter lists this pass now, at this venue (the server's `priceForCounter` checks, less the limits). */
export function onCounterShelf(product: PassProduct, venueId: string, now: Date): boolean {
  const terms = product.version;
  if (!product.active || !terms.channels.includes('counter') || terms.price.amount <= 0) return false;
  const window = terms.saleWindow;
  if (window?.availableFrom !== undefined && now.getTime() < Date.parse(window.availableFrom)) return false;
  if (window?.availableUntil !== undefined && now.getTime() > Date.parse(window.availableUntil)) return false;
  return terms.scope.type !== 'venues' || (terms.scope.venueIds ?? []).includes(venueId);
}

/** The counter's passes, in the catalogue's order. */
export function counterShelf(products: readonly PassProduct[], venueId: string, now: Date): PassProduct[] {
  return products.filter((product) => onCounterShelf(product, venueId, now)).sort((a, b) => a.sortOrder - b.sortOrder);
}
